import type { ApplicationContext, ErrorCode } from '@hc-frontend/core-context';
import type { IErrorOperationResult } from '@hc-frontend/core-utils-business-operation';

import { LeadProductOptions } from '../../entities';
import type { JourneyRepository, LeadProductResult } from '../../repositories';

export type UpdateLeadProductUseCase = (
  options: LeadProductOptions,
) => Promise<LeadProductResult>;

function logOperation<T extends string>(
  log: ApplicationContext['log'],
  result: IErrorOperationResult<T>,
): IErrorOperationResult<T> {
  log({
    context: 'server',
    message: result.errors[0].code,
    type: 'error',
    data: {
      rootCause: result.errors[0].rootCause,
    },
  });

  return result;
}

export type UpdateLeadProductFactoryParams = Pick<
  JourneyRepository,
  'updateLeadProduct'
> & {
  log: ApplicationContext['log'];
};

export function updateLeadProductFactory(
  provider: UpdateLeadProductFactoryParams,
): UpdateLeadProductUseCase {
  return async (options) => {
    const parsedOptions = LeadProductOptions.safeParse(options);

    if (!parsedOptions.success) {
      return logOperation<ErrorCode<'failedToUpdateLeadProduct'>>(
        provider.log,
        {
          success: false,
          errors: [
            {
              code: 'failedToUpdateLeadProduct',
              rootCause: parsedOptions.error,
            },
          ],
        },
      );
    }

    const result = await provider.updateLeadProduct(parsedOptions.data);

    if (!result.success) {
      return logOperation(provider.log, result);
    }

    return result;
  };
}
